import { FormBuilder } from '@angular/forms';
import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, Validators } from '@angular/forms';
import { AppMaterialModule } from '../../../app-material.module';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../shared/services/auth.service';
import { CadastroRequest } from '../../../shared/models/auth/cadastro-request.model';
import { ApiResult } from '../../../shared/models/api-result.models';
import { NavigationService } from '../../../shared/services/navigation.service';
import { ValidatorService } from '../../../shared/services/validator-service/validator.service';
import { ActivatedRoute } from '@angular/router';
import { AuthRightColumnComponent } from '../../../shared/components/auth-right-column/auth-right-column.component';
import { LanguageToggleComponent } from '../../../shared/components/language-toggle/language-toggle.component';
import { ThemeSwitcherComponent } from '../../../shared/components/theme-switcher/theme-switcher.component';
import { TranslocoPipe } from '@jsverse/transloco';
import { TranslocoService } from '@jsverse/transloco';
import { phoneMask } from '../../../shared/uteis/form-validations';
import { ThemeService } from '../../../shared/services/theme.service';
import { buildHubLoginUrlWithReturn } from '../../../shared/utils/hub-redirect';

@Component({
    selector: 'app-cadastro',
    standalone: true,
    imports: [
        CommonModule,
        AppMaterialModule,
        AuthRightColumnComponent,
        LanguageToggleComponent,
        ThemeSwitcherComponent,
        TranslocoPipe,
    ],
    templateUrl: './cadastro.component.html',
    styleUrl: './cadastro.component.scss',
})
export class CadastroComponent implements OnInit {
    public loginForm: FormGroup = new FormGroup({});

    public get hubLoginUrl(): string {
        if (typeof window === 'undefined') {
            return '';
        }
        return buildHubLoginUrlWithReturn(`${window.location.origin}/v2`);
    }

    public showPassword = false;
    public iconName = 'visibility';
    public useDDI = false;

    constructor(
        private formBuilder: FormBuilder,
        private toastrService: ToastrService,
        private authService: AuthService,
        private navigationService: NavigationService,
        private validatorService: ValidatorService,
        private activatedRoute: ActivatedRoute,
        private transloco: TranslocoService,
        public themeService: ThemeService,
    ) {
        if (this.authService.isAtuthenticated()) {
            this.navigationService.goTo('/painel');
            return;
        }

        this.loginForm = this.formBuilder.group({
            nome: ['', [Validators.required]],
            documento: ['', [Validators.required]],
            celular_cript: ['', [Validators.required, this.validatorService.phoneWithDDIValidator()]],
            email: ['', [Validators.required, Validators.email]],
            senha: ['', [Validators.required, Validators.minLength(12), this.validatorService.passwordValidator()]],
            track_list: [''], // Campo para armazenar parâmetros de tracking em JSON
        });
    }

    ngOnInit() {
        this.loadUrlParams();
        this.setupPhoneMask();
        this.setupCpfMask();
        this.loadTrackList();
    }

    private loadUrlParams() {
        const queryParams = this.activatedRoute.snapshot.queryParams;

        // Carrega name, celular/whatsapp, email da URL
        const queryParamNome = queryParams['nome'] || queryParams['name'];

        if (queryParamNome) {
            this.loginForm.patchValue({ nome: queryParamNome });
        }

        const queryParamPhone = queryParams['phone'] || queryParams['celular'] || queryParams['whatsapp'];
        if (queryParamPhone) {
            // Detecta se tem DDI no número
            this.useDDI = this.detectDDI(queryParamPhone);
            // Aplica máscara baseada na detecção de DDI
            this.loginForm.patchValue({
                celular: phoneMask(queryParamPhone, this.useDDI),
            });
        }

        if (queryParams['email']) {
            this.loginForm.patchValue({ email: queryParams['email'] });
        }

        const queryParamDocumento = queryParams['documento'] || queryParams['cpf'];
        if (queryParamDocumento) {
            this.loginForm.patchValue({ documento: queryParamDocumento });
        }
    }

    private detectDDI(phoneValue: string): boolean {
        if (!phoneValue) return false;

        // Remove tudo que não é número
        const numbers = phoneValue.replace(/\D/g, '');

        // Se tem menos de 10 dígitos, provavelmente não tem DDI completo
        if (numbers.length < 10) return false;

        // Verifica se começa com DDI conhecido (prioriza 55 - Brasil)
        // DDI de 1 dígito: 1, 7
        // DDI de 2 dígitos: 10-99 (mais comuns: 55, 44, 33, 49, etc.)
        // DDI de 3 dígitos: 200-999

        // Prioriza números brasileiros (DDI 55)
        if (numbers.startsWith('55') && numbers.length >= 12) {
            return true;
        }

        // Verifica DDI de 3 dígitos (212, 213, etc.)
        if (numbers.length >= 10 && /^[2-9]\d{2}/.test(numbers)) {
            const potentialDDI = numbers.substring(0, 3);
            const rest = numbers.substring(3);
            // Se o restante tem pelo menos 7 dígitos, provavelmente é DDI
            if (rest.length >= 7) {
                return true;
            }
        }

        // Verifica DDI de 2 dígitos (mas não 55 que já foi tratado)
        if (numbers.length >= 9 && /^[1-9]\d/.test(numbers) && !numbers.startsWith('55')) {
            const potentialDDI = numbers.substring(0, 2);
            const rest = numbers.substring(2);
            const ddiNum = parseInt(potentialDDI, 10);
            // DDI de 2 dígitos válidos (10-99)
            if (ddiNum >= 10 && ddiNum <= 99 && rest.length >= 7) {
                return true;
            }
        }

        // Verifica DDI de 1 dígito (1, 7)
        if (numbers.length >= 8 && /^[17]/.test(numbers)) {
            const rest = numbers.substring(1);
            if (rest.length >= 7) {
                return true;
            }
        }

        // Se tem 10 ou 11 dígitos, provavelmente é formato brasileiro sem DDI
        if (numbers.length === 10 || numbers.length === 11) {
            return false;
        }

        // Se tem mais de 11 dígitos, provavelmente tem DDI
        return numbers.length > 11;
    }

    private setupPhoneMask() {
        this.loginForm.get('celular_cript')?.valueChanges.subscribe((value) => {
            if (value) {
                const hasDDI = this.validatorService.detectDDI(value);
                if (hasDDI !== this.useDDI) {
                    this.useDDI = hasDDI;
                }
                const maskedValue = this.validatorService.applyPhoneMask(value, this.useDDI);
                if (maskedValue !== value) {
                    this.loginForm.get('celular_cript')?.setValue(maskedValue, { emitEvent: false });
                }
            } else {
                this.useDDI = false;
            }
        });
    }

    private setupCpfMask() {
        this.loginForm.get('documento')?.valueChanges.subscribe((value) => {
            if (value) {
                const maskedValue = this.validatorService.applyCpfMask(value);
                if (maskedValue !== value) {
                    this.loginForm.get('documento')?.setValue(maskedValue, { emitEvent: false });
                }
            }
        });
    }

    getCelularErrorMessage(): string | null {
        const celularControl = this.loginForm.get('celular_cript');
        if (celularControl?.invalid && celularControl?.touched) {
            if (celularControl.errors?.['required']) {
                return this.transloco.translate('auth.cadastro.whatsappRequired');
            }
            if (celularControl.errors?.['invalidPhone']) {
                return celularControl.errors['invalidPhone'];
            }
        }
        return null;
    }

    /**
     * Carrega os parâmetros de tracking da URL e armazena no campo track_list como JSON
     */
    private loadTrackList(): void {
        const queryParams = this.activatedRoute.snapshot.queryParams;
        const acceptedParams = this.validatorService.getAcceptedTrackingParams();
        const trackParams: { [key: string]: string } = {};

        // Parâmetros a serem ignorados (dados pessoais e outros)
        const ignoredParams = ['nome', 'celular', 'whatsapp', 'email', 'documento', 'cpf'];

        // Adiciona parâmetros aceitos
        acceptedParams.forEach((param) => {
            if (queryParams[param] && !ignoredParams.includes(param)) {
                trackParams[param] = queryParams[param];
            }
        });

        // Adiciona outros parâmetros que não sejam ignorados e não estejam na lista de aceitos
        // (para permitir parâmetros customizados de tracking)
        Object.keys(queryParams).forEach((key) => {
            if (
                !ignoredParams.includes(key) &&
                !acceptedParams.includes(key) &&
                queryParams[key] &&
                // Aceita apenas parâmetros que parecem ser de tracking (não começam com caracteres especiais)
                /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(key)
            ) {
                trackParams[key] = queryParams[key];
            }
        });

        // Converte para JSON e armazena no campo
        if (Object.keys(trackParams).length > 0) {
            const trackListJson = JSON.stringify(trackParams);
            this.loginForm.patchValue({ track_list: trackListJson });
        }
    }

    public chageShowPassword() {
        this.showPassword = !this.showPassword;
        this.iconName = this.showPassword ? 'visibility_off' : 'visibility';
    }

    get senha() {
        return this.loginForm.get('senha');
    }

    onSubmit() {
        // Lógica para lidar com o login, por exemplo, enviar os dados para um serviço

        if (this.loginForm.invalid) {
            this.toastrService.error(this.transloco.translate('auth.cadastro.invalidData'));
            return;
        }

        const { nome, senha, email, celular_cript, documento, track_list } = this.loginForm.value;

        const request = {
            nome,
            senha,
            email,
            celular_cript,
            documento,
            track_list: track_list || {},
            parcial: 0,
        } as CadastroRequest;

        this.authService.cadastro(request).subscribe({
            next: (result: ApiResult) => {
                const retorno = result.data;

                if (retorno && retorno.accessToken) {
                    this.toastrService.success(result.message);
                    localStorage.setItem('accessToken', retorno.accessToken);
                }

                window.open('https://ratoeiraads.com.br/#planos', '_blank');
            },
            error: (e) => {
                this.toastrService.error(e.error.message);
            },
            complete: () => {
                this.navigationService.goTo('/v2');
            },
        });
    }
}
